import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import TableExportActions from './common/TableExportActions';
import { useAuth } from '../contexts/AuthContext';
import { isAdminColegio } from '../utils/adminMenu';

const emptyForm = {
  email: '',
  dni: '',
  nombre: '',
  apellido: '',
  telefono: ''
};

const getValue = (source, keys, fallback = '') => {
  if (!source) return fallback;
  const value = keys.map((key) => source[key]).find((item) => item !== undefined && item !== null);
  return value ?? fallback;
};

const normalizeProfesor = (item) => ({
  id: getValue(item, ['id', 'Id']),
  email: getValue(item, ['email', 'Email']),
  dni: getValue(item, ['dni', 'Dni']),
  nombre: getValue(item, ['nombre', 'Nombre']),
  apellido: getValue(item, ['apellido', 'Apellido']),
  displayName: getValue(item, ['displayName', 'DisplayName']),
  telefono: getValue(item, ['telefono', 'Telefono']),
  activo: Boolean(getValue(item, ['activo', 'Activo'], true)),
  esAdministrador: Boolean(getValue(item, ['esAdministrador', 'EsAdministrador'], false))
});

const getProfesorNombre = (profesor) => {
  if (!profesor) return 'el profesor';
  return `${profesor.nombre || ''} ${profesor.apellido || ''}`.trim() || profesor.displayName || profesor.email || 'el profesor';
};

const getProfesorResetPassword = (profesor) => String(profesor?.dni || '').trim();

const AdminProfesorParticularProfesores = () => {
  const navigate = useNavigate();
  const { particularId } = useParams();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [particularName, setParticularName] = useState('Particular');
  const [form, setForm] = useState(emptyForm);
  const [profesores, setProfesores] = useState([]);
  const [profesoresLoading, setProfesoresLoading] = useState(false);
  const [profesoresError, setProfesoresError] = useState('');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [editModal, setEditModal] = useState({ open: false, profesor: null });
  const [editForm, setEditForm] = useState(emptyForm);
  const [toggleModal, setToggleModal] = useState({ open: false, profesor: null, nextActivo: true });
  const [resetModal, setResetModal] = useState({ open: false, profesor: null });
  const [resetResult, setResetResult] = useState({ open: false, ok: true, message: '' });

  const filteredProfesores = useMemo(() => {
    if (!search.trim()) return profesores;
    const term = search.toLowerCase();
    return profesores.filter((item) => {
      const nombre = `${item.nombre || ''} ${item.apellido || ''}`.trim().toLowerCase();
      const display = (item.displayName || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      return nombre.includes(term) || display.includes(term) || email.includes(term);
    });
  }, [profesores, search]);

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    const loadParticular = async () => {
      if (!particularId) return;
      try {
        const response = await profesorParticularApiService.getProfesorParticularById(particularId);
        const reg = response?.profesorParticular || response?.ProfesorParticular || response;
        setParticularName(getValue(reg, ['nombreVisible', 'NombreVisible', 'nombre', 'Nombre'], 'Particular'));
      } catch (error) {
        setParticularName('Particular');
        setMessage(error.message || 'No pudimos cargar el particular.');
        setMessageType('error');
      }
    };

    loadParticular();
  }, [particularId]);

  const loadProfesores = useCallback(async () => {
    if (!particularId) return;
    setProfesoresLoading(true);
    setProfesoresError('');
    try {
      const list = await profesorParticularApiService.getProfesores(particularId);
      setProfesores(Array.isArray(list) ? list.map(normalizeProfesor) : []);
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos cargar los profesores.');
    } finally {
      setProfesoresLoading(false);
    }
  }, [particularId]);

  useEffect(() => {
    loadProfesores();
  }, [loadProfesores]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (values) => {
    if (!values.email.trim()) return 'Completa email para continuar.';
    if (!values.dni.trim()) return 'Completa DNI para continuar.';
    if (values.dni.trim().length < 6) return 'El DNI debe tener al menos 6 caracteres.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validateForm(form);
    if (validation) {
      setMessage(validation);
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando profesor...');
    setMessageType('info');

    try {
      await profesorParticularApiService.createProfesor(particularId, form);
      setMessage('Profesor creado correctamente.');
      setMessageType('success');
      setForm(emptyForm);
      await loadProfesores();
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el profesor.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (profesor) => {
    setEditForm({
      email: profesor.email || '',
      dni: profesor.dni || '',
      nombre: profesor.nombre || '',
      apellido: profesor.apellido || '',
      telefono: profesor.telefono || ''
    });
    setEditModal({ open: true, profesor });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, profesor: null });
  };

  const handleSaveEdit = async () => {
    if (!editModal.profesor) return;
    const validation = validateForm(editForm);
    if (validation) {
      setProfesoresError(validation);
      return;
    }

    setActionLoading(true);
    setProfesoresError('');
    setMessage('Actualizando profesor...');
    setMessageType('info');

    try {
      await profesorParticularApiService.updateProfesor(particularId, editModal.profesor.id, editForm);
      await loadProfesores();
      closeEditModal();
      setMessage('Profesor actualizado correctamente.');
      setMessageType('success');
    } catch (error) {
      const text = error.message || 'No pudimos actualizar el profesor.';
      setProfesoresError(text);
      setMessage(text);
      setMessageType('error');
    } finally {
      setActionLoading(false);
    }
  };

  const openToggleModal = (profesor) => {
    setToggleModal({ open: true, profesor, nextActivo: !profesor.activo });
  };

  const closeToggleModal = () => {
    setToggleModal({ open: false, profesor: null, nextActivo: true });
  };

  const handleToggleActivo = async () => {
    if (!toggleModal.profesor) return;
    setActionLoading(true);
    setProfesoresError('');
    try {
      await profesorParticularApiService.updateProfesorActivo(
        particularId,
        toggleModal.profesor.id,
        toggleModal.nextActivo
      );
      await loadProfesores();
      closeToggleModal();
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos actualizar el estado del profesor.');
    } finally {
      setActionLoading(false);
    }
  };

  const openResetModal = (profesor) => {
    setResetModal({ open: true, profesor });
  };

  const closeResetModal = () => {
    setResetModal({ open: false, profesor: null });
  };

  const handleResetPassword = async () => {
    if (!resetModal.profesor) return;
    const newPassword = getProfesorResetPassword(resetModal.profesor);
    if (!newPassword) {
      closeResetModal();
      setResetResult({
        open: true,
        ok: false,
        message: 'No se puede resetear la contraseña porque el profesor no tiene DNI registrado.'
      });
      return;
    }

    setActionLoading(true);
    setProfesoresError('');
    try {
      await profesorParticularApiService.resetProfesorPassword(particularId, resetModal.profesor.id);
      closeResetModal();
      setResetResult({ open: true, ok: true, message: `Contraseña reseteada. Nuevo password: ${newPassword}.` });
    } catch (error) {
      closeResetModal();
      setResetResult({ open: true, ok: false, message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Profesores de particular</p>
          <h1>Alta de profesor</h1>
          <p className="subtitle">Particular: {particularName}</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveColegioBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a particulares</span>
          </button>
          <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-plus" />
              <span>Datos del profesor</span>
            </div>
          </div>
          <div className="card-content">
            <p style={{ marginTop: 0, color: '#475569' }}>
              Completa los datos requeridos. <strong>La contraseña inicial será el DNI</strong>; el usuario podrá cambiarla luego.
            </p>
            <form onSubmit={handleSubmit} className="prof-create-form">
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="profesor@ejemplo.com"
                />
              </div>
              <div className="form-field">
                <label>DNI</label>
                <input
                  type="text"
                  value={form.dni}
                  onChange={(e) => handleChange('dni', e.target.value)}
                  placeholder="12345678"
                />
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre</label>
                  <input type="text" value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input type="text" value={form.apellido} onChange={(e) => handleChange('apellido', e.target.value)} />
                </div>
              </div>
              <div className="form-field">
                <label>Teléfono</label>
                <input type="text" value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} />
              </div>

              {message && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.5rem',
                    borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {message}
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                  <span>Crear profesor</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-users" />
              <span>Profesores del particular</span>
            </div>
            <div className="professor-actions">
              <div className="professor-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="refresh-btn" type="button" onClick={loadProfesores} disabled={profesoresLoading}>
                <i className={`fas ${profesoresLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              </button>
              <TableExportActions
                tableSelector="#admin-particular-profesores-table"
                fileName={`profesores-particular-${particularName || 'particular'}`}
                title={`Profesores de ${particularName || 'particular'}`}
                compact
                disabled={filteredProfesores.length === 0}
              />
            </div>
          </div>
          <div className="card-content professors-table">
            {profesoresLoading && (
              <div className="data-table-loading">
                <div className="loading-spinner"><i className="fas fa-spinner fa-spin" /></div>
                <p>Cargando profesores...</p>
              </div>
            )}

            {!profesoresLoading && profesoresError && (
              <div className="data-table-empty">
                <div className="empty-icon"><i className="fas fa-exclamation-triangle" /></div>
                <p>{profesoresError}</p>
                <button className="refresh-btn" type="button" onClick={loadProfesores}>
                  <i className="fas fa-sync-alt" /> Reintentar
                </button>
              </div>
            )}

            {!profesoresLoading && !profesoresError && filteredProfesores.length === 0 && (
              <div className="data-table-empty">
                <div className="empty-icon"><i className="fas fa-users-slash" /></div>
                <p>No hay profesores cargados.</p>
              </div>
            )}

            {!profesoresLoading && !profesoresError && filteredProfesores.length > 0 && (
              <div className="data-table-wrapper">
                <table id="admin-particular-profesores-table" className="data-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Teléfono</th>
                      <th className="text-center width-sm">Estado</th>
                      <th className="text-center width-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProfesores.map((profesor) => {
                      const nombre = `${profesor.nombre || ''} ${profesor.apellido || ''}`.trim() || profesor.displayName || '-';
                      return (
                        <tr key={profesor.id || profesor.email}>
                          <td>
                            {nombre}
                            {profesor.esAdministrador && (
                              <span className="status-chip info" style={{ marginLeft: '0.5rem' }}>Administrador</span>
                            )}
                          </td>
                          <td>{profesor.email || '-'}</td>
                          <td>{profesor.telefono || '-'}</td>
                          <td className="text-center">
                            <span className={`status-chip ${profesor.activo ? 'success' : 'danger'}`}>
                              {profesor.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="action-buttons">
                              <button type="button" className="action-edit-btn" onClick={() => openEditModal(profesor)} title="Editar profesor">
                                <i className="fas fa-edit" />
                              </button>
                              <button type="button" className="action-reset-btn" onClick={() => openResetModal(profesor)} title="Resetear contraseña">
                                <i className="fas fa-key" />
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${profesor.activo ? 'danger' : 'success'}`}
                                onClick={() => openToggleModal(profesor)}
                                disabled={actionLoading}
                                title={profesor.activo ? 'Desactivar profesor' : 'Activar profesor'}
                              >
                                <i className={`fas ${profesor.activo ? 'fa-ban' : 'fa-check-circle'}`} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className="fas fa-user-edit" /></div>
            <h3>Editar profesor</h3>
            <p>Actualiza los datos del profesor del particular.</p>
            <div className="modal-form">
              <div className="form-field">
                <label>Email</label>
                <input type="email" value={editForm.email} onChange={(e) => handleEditChange('email', e.target.value)} />
              </div>
              <div className="form-field">
                <label>DNI</label>
                <input type="text" value={editForm.dni} onChange={(e) => handleEditChange('dni', e.target.value)} />
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre</label>
                  <input type="text" value={editForm.nombre} onChange={(e) => handleEditChange('nombre', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input type="text" value={editForm.apellido} onChange={(e) => handleEditChange('apellido', e.target.value)} />
                </div>
              </div>
              <div className="form-field">
                <label>Teléfono</label>
                <input type="text" value={editForm.telefono} onChange={(e) => handleEditChange('telefono', e.target.value)} />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeEditModal} disabled={actionLoading}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className={`fas ${toggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} /></div>
            <h3>{toggleModal.nextActivo ? 'Activar profesor' : 'Desactivar profesor'}</h3>
            <p>
              {toggleModal.nextActivo
                ? 'El profesor volverá a estar activo en este particular.'
                : 'El profesor dejará de estar activo en este particular.'}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeToggleModal} disabled={actionLoading}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleToggleActivo} disabled={actionLoading}>
                {actionLoading ? 'Actualizando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className="fas fa-key" /></div>
            <h3>Resetear contraseña</h3>
            <p>
              Esta acción reemplazará la contraseña actual de {getProfesorNombre(resetModal.profesor)}.
              El usuario dejará de acceder con su password anterior.
            </p>
            <div
              className="modal-message"
              style={{
                borderColor: getProfesorResetPassword(resetModal.profesor) ? '#bfdbfe' : '#fecaca',
                background: getProfesorResetPassword(resetModal.profesor) ? '#eff6ff' : '#fee2e2',
                color: getProfesorResetPassword(resetModal.profesor) ? '#1e3a8a' : '#7f1d1d'
              }}
            >
              Nuevo password:{' '}
              <strong>{getProfesorResetPassword(resetModal.profesor) || 'Sin DNI registrado'}</strong>
            </div>
            <p>Se mantendrán sus datos, permisos y estado de acceso.</p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeResetModal} disabled={actionLoading}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleResetPassword}
                disabled={actionLoading || !getProfesorResetPassword(resetModal.profesor)}
              >
                {actionLoading ? 'Reseteando...' : 'Confirmar reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetResult.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resetResult.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resetResult.ok ? 'Contraseña reseteada' : 'No se pudo resetear'}</h3>
            <p>{resetResult.message}</p>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={() => setResetResult({ open: false, ok: true, message: '' })}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfesorParticularProfesores;
